import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import { getString } from '@/lib/stringUtils';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

interface Props {
    businesses: BusinessItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const cityToProvinceMap: Record<string, string> = {
    Johannesburg: 'gauteng',
    Pretoria: 'gauteng',
    'Cape Town': 'western-cape',
    Durban: 'kwazulu-natal',
    Gqeberha: 'eastern-cape',
    Bloemfontein: 'free-state',
};

const quickProvinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Mpumalanga',
    'Limpopo',
    'North West',
    'Northern Cape',
];

export default function TowTruckNearMePage({ businesses, pagination }: Props) {
    const router = useRouter();
    const { query } = router;

    const searchParams = {
        category: getString(query.category),
        province: getString(query.province),
        city: getString(query.city),
        suburb: getString(query.suburb),
        text: '',
        page: query.page ? parseInt(query.page as string, 10) : 1,
    };

    const toTitleCase = (value: string) =>
        value
            .split('-')
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');

    const provinceName = searchParams.province ? toTitleCase(searchParams.province) : '';
    const cityName = searchParams.city ? toTitleCase(searchParams.city) : '';
    const suburbName = searchParams.suburb ? toTitleCase(searchParams.suburb) : '';
    const hasLocationFilter = Boolean(provinceName || cityName || suburbName);
    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;

    const locationSuffix = hasLocationFilter ? `in ${locationLabel}` : 'in South Africa';
    const titleLocationLabel = hasLocationFilter ? `in ${locationLabel}` : 'Near Me';

    const pageTitle = hasLocationFilter
        ? `Tow Truck ${locationLabel} | Emergency Towing & Recovery Services`
        : 'Tow Truck Near Me | Emergency Towing & Recovery Services';

    const pageDescription = hasLocationFilter
        ? `Find a tow truck ${locationLabel}. Fast emergency vehicle towing, breakdown recovery, and roadside assistance. Available 24/7 across South Africa. Call now.`
        : 'Find a tow truck near me. Fast emergency vehicle towing, breakdown recovery, and roadside assistance. Available 24/7 across South Africa. Get help now.';

    const baseUrl = `${ENV.DOMAIN_URL}/tow-truck-near-me`;
    const canonicalParams = new URLSearchParams();
    if (searchParams.province) canonicalParams.set('province', searchParams.province);
    if (searchParams.city) canonicalParams.set('city', searchParams.city);
    if (searchParams.suburb) canonicalParams.set('suburb', searchParams.suburb);
    if (searchParams.page > 1) canonicalParams.set('page', String(searchParams.page));
    const canonicalUrl = canonicalParams.toString()
        ? `${baseUrl}?${canonicalParams.toString()}`
        : baseUrl;

    const suburbLinksByCity: Record<string, string[]> = {
        Johannesburg: ['Sandton', 'Randburg', 'Roodepoort', 'Soweto', 'Midrand'],
        Pretoria: ['Centurion', 'Hatfield', 'Menlyn', 'Brooklyn', 'Silverton'],
        'Cape Town': ['Bellville', 'Century City', 'Milnerton', 'Woodstock', 'Somerset West'],
        Durban: ['Umhlanga', 'Pinetown', 'Westville', 'Berea', 'Amanzimtoti'],
        Gqeberha: ['Walmer', 'Newton Park', 'Summerstrand', 'Korsten', 'Uitenhage'],
        Bloemfontein: ['Universitas', 'Langenhoven Park', 'Brandwag', 'Westdene', 'Hilton'],
    };

    const suburbLinks = cityName && suburbLinksByCity[cityName] ? suburbLinksByCity[cityName] : [];
    const locationKeywords = hasLocationFilter ? locationLabel : 'South Africa';
    const keywordList = [
        `tow truck ${locationKeywords}`,
        `tow truck near me ${locationKeywords}`,
        `emergency towing ${locationKeywords}`,
        `vehicle recovery ${locationKeywords}`,
        `breakdown recovery ${locationKeywords}`,
        `roadside assistance ${locationKeywords}`,
        `car towing service ${locationKeywords}`,
    ].join(', ');

    const faqItems = [
        {
            question: `How do I find a tow truck ${locationSuffix}?`,
            answer: hasLocationFilter
                ? `You are filtering for ${locationLabel}. Use the province, city, or suburb filters to find the nearest tow truck services, or clear filters to see all locations in South Africa.`
                : 'Use the province, city, or suburb filters to find the nearest tow truck and vehicle recovery services. You can also browse all tow truck operators across South Africa.',
        },
        {
            question: `Are tow truck services available 24/7 ${locationSuffix}?`,
            answer: 'Most tow truck operators listed here offer 24/7 emergency towing services. Whether you have a breakdown on the highway or in a suburb, emergency towing is available around the clock. Contact your nearest operator for immediate assistance.',
        },
        {
            question: 'What types of vehicles can be towed?',
            answer: 'Tow truck operators can typically tow cars, SUVs, bakkies, motorcycles, and light commercial vehicles. Some operators also offer heavy-duty towing for trucks and buses. Check with the operator for specific vehicle capabilities.',
        },
        {
            question: 'What should I do while waiting for a tow truck?',
            answer: 'Turn on your hazard lights, move to a safe location away from traffic if possible, stay inside your vehicle if on a highway, place warning triangles behind your vehicle, and call your tow truck operator to give your exact location.',
        },
        {
            question: 'How much does a tow truck cost in South Africa?',
            answer: 'Tow truck costs vary by distance, vehicle type, and operator. Typical rates range from R500 to R2000+ for local tows. Highway recoveries and longer distances will cost more. Many insurance policies include towing cover — check your policy before paying out of pocket.',
        },
    ];

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywordList} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@graph': [
                                {
                                    '@type': 'LocalBusiness',
                                    '@id': canonicalUrl,
                                    name: `Tow Truck ${titleLocationLabel}`,
                                    description: pageDescription,
                                    url: canonicalUrl,
                                    areaServed: locationKeywords,
                                    knowsAbout: [
                                        'Emergency Towing',
                                        'Vehicle Recovery',
                                        'Breakdown Assistance',
                                        'Roadside Assistance',
                                        'Accident Recovery',
                                        'Highway Towing',
                                    ],
                                },
                                {
                                    '@type': 'FAQPage',
                                    mainEntity: faqItems.map((item) => ({
                                        '@type': 'Question',
                                        name: item.question,
                                        acceptedAnswer: {
                                            '@type': 'Answer',
                                            text: item.answer,
                                        },
                                    })),
                                },
                            ],
                        }),
                    }}
                />
            </Head>

            <section className="relative overflow-hidden bg-[#0f1512] text-white">
                <div
                    className="absolute inset-0 opacity-35"
                    style={{
                        backgroundImage:
                            'radial-gradient(circle at 15% 20%, rgba(239, 68, 68, 0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(220, 38, 38, 0.3), transparent 40%), radial-gradient(circle at 65% 85%, rgba(248, 113, 113, 0.25), transparent 45%)',
                    }}
                />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.3em] text-red-200">Emergency Towing & Recovery</p>
                            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
                                Tow Truck {titleLocationLabel}
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-red-100">
                                Find emergency tow truck and vehicle recovery services near you. Fast response, 24/7
                                availability, and professional roadside assistance across South Africa.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <Link
                                    href="#business-filters"
                                    className="bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Find a tow truck
                                </Link>
                                <Link
                                    href="/tow-truck-near-me"
                                    className="bg-red-500 text-white px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    All tow truck services
                                </Link>
                                <span className="text-xs text-red-100">Quick city shortcuts below.</span>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-red-100">Popular locations</p>
                                    <span className="rounded-full bg-red-500/20 px-2.5 py-1 text-xs text-red-100">
                                        24/7
                                    </span>
                                </div>
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <Link
                                        href="/tow-truck-near-me/tow-truck/gauteng/johannesburg"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-red-300"
                                    >
                                        Tow Truck Johannesburg
                                    </Link>
                                    <Link
                                        href="/tow-truck-near-me/tow-truck/western-cape/cape-town"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-red-300"
                                    >
                                        Tow Truck Cape Town
                                    </Link>
                                    <Link
                                        href="/tow-truck-near-me/tow-truck/kwazulu-natal/durban"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-red-300"
                                    >
                                        Tow Truck Durban
                                    </Link>
                                    <Link
                                        href="/tow-truck-near-me/tow-truck/gauteng/pretoria"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-red-300"
                                    >
                                        Tow Truck Pretoria
                                    </Link>
                                </div>
                                <div className="mt-5 grid gap-3 sm:grid-cols-3 text-xs text-red-100">
                                    <div className="rounded-lg border border-white/10 px-3 py-2">24/7 emergency</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Fast response</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">All vehicle types</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-8">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-2xl font-semibold text-slate-900">Emergency Tow Truck Services Near You</h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Looking for a tow truck {locationSuffix}? CarSparesEzyFind connects you with
                                professional towing operators and vehicle recovery services across South Africa. Whether
                                you have broken down on the highway, been in an accident, or need your vehicle moved,
                                use the filters to find the nearest tow truck operator by province, city, or suburb.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Whether you need a tow truck near me in Sandton, emergency towing in Roodepoort,
                                or roadside assistance in Randburg, this page helps you find the most convenient
                                and fastest-responding tow truck service. Most operators offer 24/7 emergency call-out,
                                flatbed towing, and vehicle recovery for accidents and breakdowns.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Popular searches include tow truck Johannesburg, emergency towing Gauteng,
                                vehicle recovery Cape Town, and breakdown assistance Durban.
                                Services include flatbed towing, vehicle recovery, accident towing, highway breakdown
                                assistance, heavy-duty towing, and motorcycle recovery.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Always check whether your motor vehicle insurance policy includes towing cover before
                                calling a tow truck. Many comprehensive policies offer free towing up to a set
                                distance. If you are not insured, compare rates from multiple operators listed here.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-slate-900">Tow Truck Services</h3>
                            <ul className="mt-4 space-y-2 text-sm text-slate-600">
                                <li>Flatbed towing & transport</li>
                                <li>Emergency breakdown recovery</li>
                                <li>Accident & crash recovery</li>
                                <li>Highway & roadside assistance</li>
                                <li>Motorcycle towing</li>
                                <li>Heavy-duty vehicle towing</li>
                            </ul>
                            <h3 className="mt-6 text-lg font-semibold text-slate-900">Emergency Tips</h3>
                            <p className="mt-3 text-sm text-slate-600">
                                If you break down, switch on hazard lights immediately, move off the road safely,
                                place warning triangles, and call a tow truck. Stay inside your vehicle on busy roads.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-6 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">Find tow trucks by city</h2>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Fast links to towing services in major cities and metro areas.
                                    </p>
                                </div>
                                <Link href="/tow-truck-near-me" className="text-sm font-semibold text-red-700">
                                    View all locations
                                </Link>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickCities.map((city) => (
                                    <Link
                                        key={city.name}
                                        href={`/tow-truck-near-me/tow-truck/${city.province}/${slugify(city.name)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-red-400 hover:text-red-700"
                                    >
                                        {city.name}
                                    </Link>
                                ))}
                            </div>
                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 1</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Choose location</p>
                                    <p className="mt-1 text-xs text-slate-600">Select your province, city, or suburb.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 2</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Find nearest tow truck</p>
                                    <p className="mt-1 text-xs text-slate-600">View closest towing operators.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 3</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Call for help</p>
                                    <p className="mt-1 text-xs text-slate-600">Contact the operator for fast response.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">Browse by province</h2>
                            <p className="text-sm text-slate-600 mt-1">
                                Filter by province, then drill down to city and suburb.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickProvinces.map((province) => (
                                    <Link
                                        key={province}
                                        href={`/tow-truck-near-me/tow-truck/${slugify(province)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-red-400 hover:text-red-700"
                                    >
                                        {province}
                                    </Link>
                                ))}
                            </div>
                        </div>
                        <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 p-5">
                            <h3 className="text-sm font-semibold text-slate-900">Breakdown tips</h3>
                            <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                <li>Always switch on hazard lights immediately.</li>
                                <li>Check your insurance policy for towing cover.</li>
                                <li>Place warning triangles at least 45m behind your vehicle.</li>
                                <li>Stay clear of traffic on busy roads and highways.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10" aria-labelledby="suburb-links-heading">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 id="suburb-links-heading" className="text-xl font-semibold text-slate-900">
                        {cityName
                            ? `Tow Truck Services by Suburb in ${cityName}`
                            : 'Tow Truck Services by City in South Africa'}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                        {cityName
                            ? `Explore towing and recovery services in ${cityName} suburbs to find your nearest operator.`
                            : 'Jump to popular cities and metros to find tow truck operators near you.'}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {cityName && suburbLinks.length > 0
                            ? suburbLinks.map((suburb) => (
                                  <Link
                                      key={suburb}
                                      href={`/tow-truck-near-me?city=${slugify(cityName)}&suburb=${slugify(suburb)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-red-400 hover:text-red-700"
                                  >
                                      {`Tow Truck ${suburb}`}
                                  </Link>
                              ))
                            : quickCities.map((city) => (
                                  <Link
                                      key={city.name}
                                      href={`/tow-truck-near-me/tow-truck/${city.province}/${slugify(city.name)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-red-400 hover:text-red-700"
                                  >
                                      {`Tow Truck ${city.name}`}
                                  </Link>
                              ))}
                    </div>
                </div>
            </section>

            <div className="my-10">
                <div className="container" id="business-filters">
                    <BusinessList
                        searchParams={searchParams}
                        defaultCategoryIds="1772"
                        initialBusinesses={businesses}
                        initialPagination={pagination}
                    />
                </div>
            </div>

            <section className="container my-12">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <p className="text-xs uppercase tracking-[0.25em] text-red-700">Tow Truck Directory</p>
                            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                                Towing & recovery services {locationSuffix}
                            </h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Use this page to find a tow truck near you, or drill into a specific province,
                                city, or suburb. Results update instantly as you apply filters.
                            </p>
                            <p className="mt-4 text-sm text-slate-600">
                                Browse tow truck operators, breakdown recovery services, and roadside assistance
                                providers in one place. Compare locations, services, and contact details side-by-side.
                            </p>
                            <div className="mt-5 rounded-xl bg-slate-50 p-4 text-xs text-slate-600">
                                {hasLocationFilter
                                    ? `Showing tow truck services for ${locationLabel}. Change province, city, or suburb to explore nearby areas.`
                                    : 'Tip: Start with a province, then narrow by city or suburb for your nearest tow truck operator.'}
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">
                                Frequently Asked Questions (Tow Truck Services)
                            </h2>
                            <div className="mt-4 space-y-4">
                                {faqItems.map((item) => (
                                    <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                        <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                        <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <DownloadApp />
        </>
    );
}

export const getStaticProps = async () => {
    const apolloClient = initializeApollo();
    const businessRequest: SearchBusinessRequest = {
        companyId: null,
        companyName: '',
        franchiseeId: null,
        statusIds: '1',
        categoryIds: '1772',
        provinceIds: '',
        cityIds: '',
        suburbIds: '',
        page: 1,
        size: 12,
    };

    const { data: businessData } = await apolloClient.query({
        query: GET_BUSINESS_LIST,
        variables: businessRequest,
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';

    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: {
                count: businessData?.getBusinessList?.count ?? 0,
                currentPage: businessData?.getBusinessList?.currentPage ?? 1,
                totalPages: businessData?.getBusinessList?.totalPages ?? 0,
            },
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
